import { LinkButton, Notice, PageHeader, Skeleton, Tabs } from '@mogzo/ui';
import { ExternalLink, Palette, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useOutletContext, useParams } from 'react-router';
import { PLAYER_SITE, type Project } from '../lib/api';
import { useCrumbs } from '../lib/crumbs';
import { formatDate } from '../lib/format';
import { useApi } from '../lib/use-api';
import { ProjectStatusChip, SuspendedNotice } from './ProjectStatus';

/** What every project tab gets from the project area. */
export interface ProjectOutlet {
  project: Project;
  /** After a change on a tab, so the header shows it too. */
  setProject: (project: Project) => void;
  /** Loads the project again (its status changed on the server). */
  reload: () => void;
}

export function useProject(): ProjectOutlet {
  return useOutletContext<ProjectOutlet>();
}

const TABS = [
  { path: '', label: 'Overview' },
  { path: 'data', label: 'Data' },
  { path: 'multiplayer', label: 'Multiplayer' },
  { path: 'schedule', label: 'Schedule' },
  { path: 'listing', label: 'Store listing' },
  { path: 'submissions', label: 'Submissions' },
  { path: 'settings', label: 'Settings' },
] as const;

/**
 * A project's pages (M25): the same header and tabs on each, the tab's page below. The Game UI
 * editor fills the screen, so its button opens it in a new browser tab.
 */
export function ProjectLayout() {
  const { id = '' } = useParams();
  const location = useLocation();
  const [reloadKey, setReloadKey] = useState(0);
  const result = useApi<Project>(`/v1/dev/projects/${encodeURIComponent(id)}`, reloadKey);
  const [changed, setChanged] = useState<Project | null>(null);
  // A newly loaded project replaces one a tab set.
  useEffect(() => setChanged(null), []);
  const reload = useCallback(() => setReloadKey((n) => n + 1), []);

  const project = changed ?? (result.status === 'ready' ? result.data : null);
  const base = `/projects/${id}`;
  const tab =
    TABS.find((t) => t.path && location.pathname.startsWith(`${base}/${t.path}`)) ?? TABS[0];
  // The schedule's Trash is a page under Schedule.
  const inTrash = location.pathname.startsWith(`${base}/schedule/trash`);
  useCrumbs(
    { label: 'Projects', to: '/' },
    { label: project?.name ?? 'Project', to: base },
    ...(inTrash
      ? [{ label: tab.label, to: `${base}/schedule` }, { label: 'Trash' }]
      : [{ label: tab.label }]),
  );

  if (result.status === 'error') {
    return (
      <main className="mz-shell-content">
        <Notice tone="danger" title="Couldn’t open this project">
          {result.error.message}
        </Notice>
      </main>
    );
  }

  return (
    <>
      <div className="mz-shell-header">
        <div className="mz-shell-header-inner">
          {project ? (
            <PageHeader
              title={project.name}
              meta={
                <>
                  <ProjectStatusChip project={project} />
                  <span className="mz-mono">{project.slug}</span>
                  <span>
                    {project.liveVersion
                      ? `${project.liveVersion} is live`
                      : `Created ${formatDate(project.createdAt)}`}
                  </span>
                </>
              }
              actions={
                <>
                  <LinkButton
                    href={`${base}/ui`}
                    target="_blank"
                    icon={Palette}
                    iconRight={ExternalLink}
                    title="Opens the Game UI editor in a new tab"
                  >
                    Game UI
                  </LinkButton>
                  <LinkButton
                    href={`${PLAYER_SITE}/test/${project.slug}`}
                    target="_blank"
                    rel="noopener"
                    icon={Users}
                  >
                    Test community
                  </LinkButton>
                  {project.liveVersion && (
                    <LinkButton
                      href={`${PLAYER_SITE}/games/${project.slug}`}
                      target="_blank"
                      rel="noopener"
                      icon={ExternalLink}
                    >
                      Game page
                    </LinkButton>
                  )}
                </>
              }
              tabs={
                <Tabs label="Project pages">
                  {TABS.map((t) => (
                    <NavLink
                      key={t.path}
                      to={t.path ? `${base}/${t.path}` : base}
                      end={!t.path}
                      className="mz-tab"
                    >
                      {t.label}
                    </NavLink>
                  ))}
                </Tabs>
              }
            />
          ) : (
            <div aria-busy="true" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Skeleton width={220} height={28} />
              <Skeleton width={320} height={14} />
              <div style={{ height: 40 }} />
            </div>
          )}
        </div>
      </div>
      <main className="mz-shell-content">
        {project && (
          <>
            <SuspendedNotice project={project} />
            <Outlet context={{ project, setProject: setChanged, reload } satisfies ProjectOutlet} />
          </>
        )}
      </main>
    </>
  );
}
